import {
  ThumbDownAltOutlined,
  ThumbsUpDownOutlined,
  ThumbUpAltOutlined,
} from '@mui/icons-material';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@work4all/components/lib/components/tooltip/Tooltip';

import { Project } from '@work4all/models/lib/Classes/Project.entity';

import { formatAsFloat } from '@work4all/utils';

import { settings, useSetting } from '../../settings';

import { useProjectControllingData } from './project-controlling-columns/ProjectControllingDataProvider';
import {
  ProjectControllingCategory,
  useProjectcontrollingFields,
} from './project-controlling-columns/ProjectControllingFields';
import { useProjectControllingColumnSettings } from './project-table/hooks/use-project-controlling-columns-setting';

export type ProjectControllingCellProps = {
  children?: React.ReactNode;
  value: string | number;
  column?: {
    id: string;
    cellParams: {
      category: ProjectControllingCategory;
    };
  };
  row?: {
    original: Project;
  };
};

export const ProjectControllingCell = (props: ProjectControllingCellProps) => {
  const { data } = useProjectControllingData();
  const statistics = useMemo(() => {
    return data.find((item) => item.projectCode === props.row.original.id);
  }, [data, props.row.original.id]);

  const category = props.column.cellParams.category;
  const columnSettings = useProjectControllingColumnSettings(category);
  const fields = useProjectcontrollingFields(props.column.cellParams.category);
  const activeFields = fields;

  const projectControllingDBColors = useSetting(
    settings.projectControllingDBColors()
  );

  const projectControllingShowDecimals = useSetting(
    settings.projectControllingShowDecimals()
  );

  const projectControllingTrendDelta = useSetting(
    settings.projectControllingTrendDelta()
  );

  const { t } = useTranslation();

  return (
    <Stack direction="row" margin="-0.3rem -0.5rem">
      {activeFields
        .filter((field) => columnSettings.value[field.key] === true)
        .map((field, idx) => {
          const value = field.resolve(statistics);
          const minValue =
            projectControllingDBColors.value.target -
            projectControllingDBColors.value.delta;
          const maxValue =
            projectControllingDBColors.value.target +
            projectControllingDBColors.value.delta;

          const dbRank =
            value < minValue ? 'under' : value > maxValue ? 'over' : 'equal';

          const dBcolor =
            dbRank === 'under'
              ? 'var(--error)'
              : dbRank === 'over'
              ? 'var(--success)'
              : 'var(--alert)';

          const isDb = [
            'dbinPROCENT',
            'dbinEUR',
            'targetContributionMargin',
          ].includes(field.key);

          const isTargetContributionMargin =
            field.key === 'targetContributionMargin';
          const isPlannedTimes = field.key === 'plannedTimes';

          const showTrend = field.key === 'planVsIst';
          const trendDelta = projectControllingTrendDelta.value;
          const plannedVsActualTrend =
            value < 0 - trendDelta
              ? 'lower'
              : value > 0 + trendDelta
              ? 'greater'
              : 'equal';

          const trendComp =
            isNaN(value) || value === null ? null : (
              <Tooltip
                title={
                  formatAsFloat(value, {
                    isFinished: true,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }) + ' %'
                }
              >
                <Stack direction="row" alignItems="center" gap="0.5rem">
                  {plannedVsActualTrend === 'lower' && (
                    <ThumbDownAltOutlined color="error" />
                  )}
                  {plannedVsActualTrend === 'equal' && (
                    <ThumbsUpDownOutlined color="warning" />
                  )}
                  {plannedVsActualTrend === 'greater' && (
                    <ThumbUpAltOutlined color="success" />
                  )}
                  <Typography>
                    {t(
                      'STATISTIC.PROJECTCONTROLLING.TREND.' +
                        plannedVsActualTrend
                    )}
                  </Typography>
                </Stack>
              </Tooltip>
            );

          return (
            <Box
              textAlign={'right'}
              flex="1"
              borderLeft={idx !== 0 && '1px solid var(--ui04)'}
              padding="0 0.5rem"
              overflow="hidden"
              textOverflow="ellipsis"
              color={isDb ? dBcolor : undefined}
            >
              {statistics === undefined ? (
                <Skeleton width="100%" height="1.5rem" />
              ) : isTargetContributionMargin ? (
                <TargetContributionMargin value={value ? dbRank : undefined} />
              ) : isPlannedTimes ? (
                value ? (
                  formatAsFloat(value, {
                    isFinished: true,
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                  }) + ' h'
                ) : null
              ) : showTrend ? (
                trendComp
              ) : !value ? (
                <>&nbsp;</>
              ) : (
                formatAsFloat(
                  !projectControllingShowDecimals.value
                    ? Math.round(value)
                    : value,
                  {
                    isFinished: true,
                    maximumFractionDigits: projectControllingShowDecimals.value
                      ? 2
                      : 0,
                    minimumFractionDigits: projectControllingShowDecimals.value
                      ? 2
                      : 0,
                  }
                )
              )}
              {Boolean(value) && field.key === 'dbinPROCENT' && ' %'}
            </Box>
          );
        })}
    </Stack>
  );
};

const TargetContributionMargin = (props: {
  value?: 'over' | 'under' | 'equal';
}) => {
  const { t } = useTranslation();

  const text = {
    over: t('STATISTIC.PROJECTCONTROLLING.TARGET_CONTRIBUTION_MARGIN.OVER'),
    under: t('STATISTIC.PROJECTCONTROLLING.TARGET_CONTRIBUTION_MARGIN.UNDER'),
    equal: t('STATISTIC.PROJECTCONTROLLING.TARGET_CONTRIBUTION_MARGIN.EQUAL'),
  };

  if (!props.value) {
    return null;
  }

  return (
    <Stack width="100%" alignItems="center" direction="row" gap="0.5rem">
      {props.value === 'over' ? (
        <ThumbUpAltOutlined />
      ) : props.value === 'under' ? (
        <ThumbDownAltOutlined />
      ) : (
        <ThumbsUpDownOutlined />
      )}
      {text[props.value] && <Typography>{text[props.value]}</Typography>}
    </Stack>
  );
};
